import AppContext from 'App/AppContext';
import {
  IPActionButton,
  IPFontIcon,
  IPLabel,
  IPMessageBar,
  IPMessageBarType,
  IPScrollablePane,
  IPText,
} from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import InfoMessage from 'components/Notification/InfoMessage';
import {
  deleteIcon,
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  newIcon,
} from 'globalStyles';
import { auditEntityKeyString, AuditProcedureRef, getAuditEntityKeysOfTask } from 'models/auditCriteria';
import Tag from 'models/tag';
import Task, { TaskTypes } from 'models/tasks/task';
import { TaskKinds } from 'models/tasks/taskType';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetProceduresForEntity } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { newGuid } from 'utils/guid';
import { TaskContext } from '../../Context/TaskContext';
import SingleTask from '../../SingleTask';
import SubTaskList from '../../SubTaskList';
import { canUpdateTaskField, TaskFieldTypes } from '../../TaskAuthHelper';
import { TaskDetailDescription } from '../Details/TaskDetailDecription';
import { TaskDetailName } from '../Details/TaskDetailName';

interface IAuditTemplateTaskDetail {
  task: Task;
  orgTask: Task;
  getErrorMessage: (field: string) => string | undefined;
  //the duplicate-name verdict, which comes back from the server and so cannot be part of the
  //synchronous validation above
  nameError?: string;
  onUpdate: (newTask: Task) => void;
  isActionPending: boolean;
  tags: Tag[];
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
  onUpdateTaskForForm?: (task: Task, validateForm: boolean) => void;
  windowLevel?: number;
  navigateExternal?: boolean;
  canUpdate: boolean;
  //the names of the linked items, keyed the way the criteria are (see AuditTemplateTask): what the
  //coverage warning below calls an item
  entityNamesByKey?: Map<string, string>;
}

// The scope items a procedure audits. A procedure is meant to carry ONE: it then asks that item's
// criteria and records one result for it. Several is a supported but exceptional shape (procedures
// migrated from monitoring tasks), which is what the confirmation below is about.
const countLinkedItems = (task: Task): number => {
  return (
    task.controlIds.length +
    task.riskIds.length +
    task.processIds.length +
    task.objectiveIds.length +
    task.assetIds.length +
    task.processingActivityIds.length
  );
};

// The guided detail pane of an internal audit procedure library. The library header (series)
// explains what procedures are and how they are matched to scope items; a subtask is one
// procedure with the auditee instruction
export const AuditTemplateTaskDetail = (props: IAuditTemplateTaskDetail) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'audit', 'tasks', 'control', 'translation']);
  const [newSubTask, setNewSubTask] = useState<Task | undefined>(undefined);
  const [editSubTask, setEditSubTask] = useState<Task | undefined>(undefined);
  const [selectedSubTasks, setSelectedSubTasks] = useState<Task[]>([]);
  // The link the user just made while the "one item is better" question is open. Applied on Yes.
  const [pendingContextTask, setPendingContextTask] = useState<Task | undefined>(undefined);
  // Bumped to remount TaskContext: it keeps its own copy of the rows and only rebuilds it on mount,
  // so a refused link has to be taken off the screen by starting it over from the unchanged task.
  const [contextKey, setContextKey] = useState<number>(0);
  // The name a new procedure got from the item it was linked to: a corrected link renames it again,
  // as long as the user has not typed a name of their own over it.
  const generatedName = useRef<string | undefined>(undefined);
  // The library procedures already covering each linked item, by entity key. The audit matches ONE
  // procedure per item, so a second procedure for the same item competes with the first and one of
  // the two is never used - worth saying at the moment the link is made, not a discovery in the audit.
  const [coveringProcedures, setCoveringProcedures] = useState<Map<string, AuditProcedureRef[]>>(new Map());

  const linkedEntityKeys = getAuditEntityKeysOfTask(props.task);
  // joined to one string so the lookup below keys on the links themselves, not on the array
  const linkedKeyString = linkedEntityKeys.map((key) => auditEntityKeyString(key.entityType, key.entityId)).join('\n');

  useEffect(() => {
    if (props.task.isSeries()) return;
    const missing = linkedEntityKeys.filter(
      (key) => !coveringProcedures.has(auditEntityKeyString(key.entityType, key.entityId)),
    );
    if (missing.length === 0) return;

    const load = async () => {
      // best effort: the warning is advice, the link stands without it
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const results = await Promise.all(
          missing.map((key) => apiGetProceduresForEntity(key.entityType, key.entityId, accessToken)),
        );
        setCoveringProcedures((current) => {
          const next = new Map(current);
          missing.forEach((key, index) => next.set(auditEntityKeyString(key.entityType, key.entityId), results[index]));

          return next;
        });
      } catch {
        // see above
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedKeyString]);

  // The linked items another procedure already covers, with those procedures' names. This
  // procedure's own binding is not a clash; an unsaved sibling in the same library session cannot
  // be seen from here, and is left to the name check.
  const coverageWarnings = linkedEntityKeys
    .map((key) => {
      const entityKey = auditEntityKeyString(key.entityType, key.entityId);
      const others = (coveringProcedures.get(entityKey) ?? []).filter(
        (procedure) => procedure.taskId !== props.task.taskId,
      );

      return { entityKey, others };
    })
    .filter((warning) => warning.others.length > 0);

  // A new procedure is named after the one item it audits, the way the server names the procedures
  // it creates itself (FormatScopeItemName: "{code} {name}"): the name is what a procedure is picked
  // by, and the item is what this one is for. Only while nothing was typed - a name of the user's own
  // is never overwritten - and only for a single item; several is the migration shape, named by hand.
  const applyGeneratedName = (newTask: Task) => {
    if (newTask.taskId !== -1) return;
    const typed = (newTask.name ?? '').trim();
    if (typed && typed !== generatedName.current) return;

    const linked = (newTask.relatedEntities ?? []).filter((entity) => !entity.isDeleted);
    if (linked.length === 1) {
      generatedName.current = linked[0].entityName;
      newTask.name = linked[0].entityName;
    } else if (linked.length === 0 && typed) {
      // the item the name came from is gone, and so is the name: it was ours, not the user's
      generatedName.current = undefined;
      newTask.name = '';
    }
  };

  // The control's checks are no longer copied onto a procedure: the server projects them into the
  // procedure's FORM as read-only fields, so there is nothing to generate or copy from here.

  const iconFontSize = { fontSize: '20px', marginTop: '5px' };
  const procedureAssignmentStyles = { root: { paddingLeft: 20, paddingTop: 20 } };
  const procedureSectionStyles = { root: { paddingTop: 20 } };

  //
  // Procedures (subtasks)
  //
  const addSubTask = () => {
    const newSubTask = new Task();
    newSubTask.taskType = TaskTypes.EventTemplate;
    //a new procedure exists only in the browser, so it inherits the audit kind; the server stamps it on save
    newSubTask.taskKind = TaskKinds.InternalAudit;
    //the library is not the user's own work: it is managed here and nowhere else, like the library
    //itself (see the server-side create)
    newSubTask.hidden = true;
    newSubTask.taskTypeId = props.task.taskTypeId;
    newSubTask.taskMasterId = props.task.taskId;
    newSubTask.taskStates = appContext.globalDataCache.taskStates.items;
    newSubTask.taskStateId = newSubTask.getFirstState();
    newSubTask.id = newGuid();
    newSubTask.creatorId = appContext.user.id;
    newSubTask.creator = appContext.user;
    newSubTask.ownerId = appContext.user.id;
    newSubTask.owner = appContext.user;
    setNewSubTask(newSubTask);
  };

  const onAddSubTask = (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      if (isNew) {
        const newTask = props.task.clone();
        if (!newTask.instances) newTask.instances = [];
        newTask.instances.push(task);
        props.onUpdate(newTask);
        setNewSubTask(undefined);
      }
    }
  };

  const onEditSubTask = (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      onUpdateSubTask(task);
    }
  };

  const onRemoveSubTask = (task: Task) => {
    //not implemented, removing is done through the subtask list
  };

  const onUpdateSubTask = (task: Task) => {
    const newTask = props.task.clone();
    if (!newTask.instances) return;
    newTask.instances = newTask.instances.map((i) => (i.id === task.id ? task : i));
    props.onUpdate(newTask);
  };

  // What the OTHER procedures of this library are called, unsaved ones included. The name check in the
  // procedure editor asks the server about stored names and this list about the rest: two procedures
  // added in one session are both new, so only the library itself can tell them apart.
  const siblingNamesFor = (subTask?: Task): string[] => {
    return (props.task.instances ?? [])
      .filter((instance) => instance.id !== subTask?.id)
      .map((instance) => instance.name);
  };

  //
  // Scope items of this procedure
  //
  // Linking a SECOND item is the moment to explain what that means; everything else (the first link,
  // any removal, a follow-up relation) passes straight through. TaskContext reports an add twice -
  // once for the form, once for the task - so both are held back and replayed together on Yes.
  const linksASecondItem = (newTask: Task): boolean => {
    return countLinkedItems(newTask) >= 2 && countLinkedItems(props.task) <= 1;
  };

  const onUpdateContext = (newTask: Task) => {
    applyGeneratedName(newTask);

    if (linksASecondItem(newTask)) {
      setPendingContextTask(newTask);

      return;
    }

    props.onUpdate(newTask);
  };

  const onUpdateContextForForm = (newTask: Task, validateForm: boolean) => {
    if (linksASecondItem(newTask)) return;

    props.onUpdateTaskForForm?.(newTask, validateForm);
  };

  const removeSelectedSubTasks = () => {
    const newTask = props.task.clone();
    if (!newTask.instances) return;
    newTask.instances = newTask.instances.filter(
      (instance) => !selectedSubTasks.some((selectedSubTask) => selectedSubTask.id === instance.id),
    );
    props.onUpdate(newTask);
    setSelectedSubTasks([]);
  };

  //
  // The detail body. Rendered in a scroll pane below, like every other detail pane: the library's
  // list of procedures is as long as the tenant made it, and a single procedure grows too (the
  // description editor unfolds its toolbar on focus, the scope-item picker its results). Anything
  // rendered straight into the column would push the modal's fixed-height stack instead, and the
  // whole modal would scroll with the Save button going out of view.
  //
  const detailBody = (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {props.task.isSeries() && (
        <Fragment>
          <IPStack.Item>
            <InfoMessage message={t('audit:ProcedureLibrary.Explanation')} />
          </IPStack.Item>
          <IPStack.Item styles={procedureAssignmentStyles}>
            <IPLabel>{t('audit:ProcedureLibrary.MatchingTitle')}</IPLabel>
            <IPStack tokens={globalStackTokensGapExtraSmall}>
              <IPText variant="small">{t('audit:ProcedureLibrary.MatchingBound')}</IPText>
              <IPText variant="small">{t('audit:ProcedureLibrary.MatchingHistory')}</IPText>
              <IPText variant="small">{t('audit:ProcedureLibrary.MatchingForm')}</IPText>
              <IPText variant="small">{t('audit:ProcedureLibrary.MatchingFallback')}</IPText>
            </IPStack>
          </IPStack.Item>
        </Fragment>
      )}
      {!props.task.isSeries() && (
        <IPStack.Item>
          <InfoMessage message={t('audit:Procedure.Explanation')} />
        </IPStack.Item>
      )}
      {/* one procedure is a real task with a name and an instruction; the library itself only
          carries the list below, so it shows neither */}
      {!props.task.isSeries() && (
        <Fragment>
          <TaskDetailName
            orgTask={props.orgTask}
            task={props.task}
            canUpdate={props.canUpdate}
            onUpdate={props.onUpdate}
            getErrorMessage={props.getErrorMessage}
            errorMessage={props.nameError}
          />
          <TaskDetailDescription
            orgTask={props.orgTask}
            task={props.task}
            canUpdate={props.canUpdate}
            onUpdate={props.onUpdate}
          />
          {/* the scope items this procedure audits belong with the instruction, not on a tab of
              their own: what a procedure covers is part of reading what it is. The block sizes to
              what it lists (fitContent): the column scrolls, and with nothing linked yet the hint
              above is followed by the picker, not by an empty box */}
          <IPStack.Item styles={procedureSectionStyles}>
            <InfoMessage message={t('audit:Procedure.ContextHint')} />
          </IPStack.Item>
          {coverageWarnings.map((warning) => (
            <IPStack.Item key={warning.entityKey}>
              <IPMessageBar messageBarType={IPMessageBarType.warning}>
                {t('audit:Procedure.ItemAlreadyCovered', {
                  item: props.entityNamesByKey?.get(warning.entityKey) ?? '',
                  procedures: warning.others.map((procedure) => procedure.name).join(', '),
                })}
              </IPMessageBar>
            </IPStack.Item>
          ))}
          <IPStack.Item>
            <TaskContext
              key={contextKey}
              orgTask={props.orgTask}
              task={props.task}
              updateTask={onUpdateContext}
              updateTaskForForm={onUpdateContextForForm}
              allowEdit={canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.ChecklistEdit, appContext)}
              hideHeaders={true}
              windowLevel={props.windowLevel}
              navigateExternal={true}
              fitContent={true}
              title={t('audit:Procedure.ContextTitle')}
            />
          </IPStack.Item>
        </Fragment>
      )}
      {props.task.isSeries() && (
        <Fragment>
          <IPStack.Item styles={procedureSectionStyles}>
            <IPStack
              horizontal
              tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
              verticalAlign="start"
            >
              <IPStack.Item>
                {!appContext.isMobileView && <IPFontIcon iconName="ComplianceAudit" style={iconFontSize} />}
              </IPStack.Item>
              <IPStack.Item>
                <IPStack>
                  <IPLabel>{t('audit:ProcedureLibrary.ProceduresLabel')}</IPLabel>
                  <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
                    <IPActionButton
                      onClick={() => addSubTask()}
                      iconProps={newIcon}
                      text={t('audit:ProcedureLibrary.AddProcedure')}
                    />
                    <IPActionButton
                      iconProps={deleteIcon}
                      text={t('tasks:TabAllTasks.Commands.Delete')}
                      disabled={selectedSubTasks.length === 0}
                      onClick={() => removeSelectedSubTasks()}
                    />
                  </IPStack>
                </IPStack>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
          <IPStack.Item grow>
            <SubTaskList
              subTasks={props.task.instances ?? []}
              selectionMode={IPSelectionMode.multiple}
              isLoading={props.isActionPending}
              onSubTaskClick={(task) => {
                setEditSubTask(task);
              }}
              updateSubTasks={(newInstances) => {
                const newTask = props.task.clone();
                newTask.instances = newInstances;
                props.onUpdate(newTask);
              }}
              //a procedure is a template: it has no deadline, and its state never moves off
              //Requested - what tells two procedures apart is the description, which is why
              //that is the one column this list keeps
              hideColumns={['deadline', 'status']}
              onChangeSelection={setSelectedSubTasks}
              enableSearch={true}
              searchPlaceholder={t('audit:ProcedureLibrary.SearchPlaceholder')}
            />
          </IPStack.Item>
        </Fragment>
      )}
      {newSubTask && (
        <SingleTask
          isOpen={newSubTask !== undefined}
          task={newSubTask}
          close={() => setNewSubTask(undefined)}
          onSave={onAddSubTask}
          onRemove={() => setNewSubTask(undefined)}
          onUpdate={onUpdateSubTask}
          windowLevel={(props.windowLevel ?? 0) + 1}
          disallowSave={true}
          navigateExternal={props.navigateExternal}
          siblingNames={siblingNamesFor()}
        />
      )}
      {editSubTask && (
        <SingleTask
          isOpen={editSubTask !== undefined}
          task={editSubTask}
          close={() => setEditSubTask(undefined)}
          onSave={onEditSubTask}
          onRemove={onRemoveSubTask}
          onUpdate={onUpdateSubTask}
          windowLevel={(props.windowLevel ?? 0) + 1}
          disallowSave={true}
          navigateExternal={props.navigateExternal}
          siblingNames={siblingNamesFor(editSubTask)}
        />
      )}
    </IPStack>
  );

  //
  // Main render
  //
  return (
    <Fragment>
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>{detailBody}</IPScrollablePane>
        </IPStack.Item>
      </IPStack>
      <DialogYesNo
        hidden={pendingContextTask === undefined}
        title={t('audit:Procedure.MultiItem.Title')}
        subText={t('audit:Procedure.MultiItem.SubText')}
        onYes={() => {
          if (pendingContextTask) {
            props.onUpdateTaskForForm?.(pendingContextTask, true);
            props.onUpdate(pendingContextTask);
          }
          setPendingContextTask(undefined);
        }}
        onNo={() => {
          setPendingContextTask(undefined);
          setContextKey((key) => key + 1);
        }}
      />
    </Fragment>
  );
};
